import { useState, useEffect, useCallback } from "react";
import axios from "axios";

export const useJobEditor = (jobNo) => {
  const [jobData, setJobData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch job data if editing existing job
  useEffect(() => {
    const fetchJob = async () => {
      if (!jobNo || jobNo === "new") {
        // Initialize new job with defaults
        setJobData({
          JOB_NO: null,
          JOB_DATE: new Date(),
          ETA: null,
          COMPLETE: 0,
          PICKEDUP: 0,
          IRREPARABLE: 0,
          BOOKING: 0,
          CANCELLED: 0,
          PRICE: 0,
          EXTRA: 0,
          DISCOUNT: 0,
          PAID: 0,
          TOTAL: 0,
          BALANCE: 0,
          QUOTE: 0,
          STAFF_ID: "",
          CUSTOMER: {
            CUSTOMER_ID: 0,
            CUST_FNAME: "",
            CUST_SNAME: "",
            CUST_CONT1: "",
            CUST_CONT2: "",
            CUST_EMAIL: "",
            CUST_POSTCODE: "",
            CONT_FNAME1: "",
            CONT_SNAME1: "",
          },
          CONSOLE_TYPE: "",
          CONSOLE_NAME: "",
          CONSOLE_MODEL: "",
          CONSOLE_COLOUR: "",
          CONSOLE_DRIVE: "",
          CONSOLE_HDD: "",
          CONSOLE_SERIAL: "",
          ACCESSOR: "",
          JOB_NAME1: "",
          JOB_NAME2: "",
          JOB_COMMENTS: "",
          JOB_TYPE: 1,
        });
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const { data } = await axios.get(`/api/jobs/${jobNo}`);
        if (data && data.length > 0) {
          const job = data[0];
          // Transform the job data to include customer object
          setJobData({
            ...job,
            CUSTOMER: {
              CUSTOMER_ID: job.CUSTOMER_ID || 0,
              CUST_FNAME: job.CUST_FNAME || "",
              CUST_SNAME: job.CUST_SNAME || "",
              CUST_CONT1: job.CUST_CONT1 || "",
              CUST_CONT2: job.CUST_CONT2 || "",
              CUST_EMAIL: job.CUST_EMAIL || "",
              CUST_POSTCODE: job.CUST_POSTCODE || "",
              CONT_FNAME1: job.CONT_FNAME1 || "",
              CONT_SNAME1: job.CONT_SNAME1 || "",
            },
          });
        }
      } catch (err) {
        console.error("Error fetching job:", err);
        setError(err.response?.data?.message || "Failed to load job");
      } finally {
        setLoading(false);
      }
    };

    fetchJob();
  }, [jobNo]);

  // Update job data
  const updateJobData = useCallback((updates) => {
    setJobData((prev) => ({
      ...prev,
      ...updates,
    }));
  }, []);

  // Save job
  const handleSave = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios.post("/api/jobs/save", jobData);
      return {
        success: true,
        jobNo: response.data.jobNo,
        jobId: response.data.jobId,
      };
    } catch (err) {
      console.error("Error saving job:", err);
      setError(err.response?.data?.message || "Failed to save job");
      throw err;
    } finally {
      setLoading(false);
    }
  }, [jobData]);

  // Save and print job
  const handleSaveAndPrint = useCallback(
    async (printInternal = false) => {
      setLoading(true);
      setError(null);

      try {
        const response = await axios.post("/api/jobs/save", {
          ...jobData,
          printAfterSave: true,
          printInternal,
        });

        // Trigger print (this would need to be implemented based on your printing system)
        // For now, we'll just return success
        return {
          success: true,
          jobNo: response.data.jobNo,
          jobId: response.data.jobId,
        };
      } catch (err) {
        console.error("Error saving and printing job:", err);
        setError(err.response?.data?.message || "Failed to save and print job");
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [jobData]
  );

  return {
    jobData,
    loading,
    error,
    handleSave,
    handleSaveAndPrint,
    updateJobData,
  };
};
